import { useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { ContactList } from '@weave/schema-gen-ts/dist/schemas/phone/contacts/contact-lists/contact_lists.pb';
import { DeviceMake_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { scopedFilterAndSort } from '@frontend/filters';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { theme } from '@frontend/theme';
import { Chip, EditSimpleIcon, Table, Text, styles, useModalControl, useTooltip } from '@frontend/design-system';
import { PhoneSettingsScopedLocationFilterMenu } from '../../../common/index-table';
import { useGetContactDirectoriesForEachDeviceId } from '../hooks/use-get-contact-directories-by-device-id';
import { AssignContactDirectriesToDevices } from '../modals/assign-contact-directories-to-devices';

export type SelectedDeviceToEditType = {
  name: string;
  deviceId: string;
  contactListsIds?: string[];
  assignableDeviceIds: string[];
};

export const DeviceAssignmentsTable = () => {
  const { t } = useTranslation('phone', { keyPrefix: 'contact-directories' });
  const { settingsTenantLocation, globalAvailableLocationIds } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'globalAvailableLocationIds'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredLocations, setFilteredLocations] = useState<string[]>([]);
  const locationIds = filteredLocations.length ? filteredLocations : globalAvailableLocationIds ?? [];
  const { devices, deviceWithContactDirectories, assignableDeviceIds, isLoading } =
    useGetContactDirectoriesForEachDeviceId({
      locationIds: locationIds,
      tenantId: settingsTenantLocation?.phoneTenantId ?? '',
    });
  const [selectedDeviceToEdit, setSelectedDeviceToEdit] = useState<SelectedDeviceToEditType>();
  const { modalProps, triggerProps } = useModalControl();

  const filteredDevices = useMemo(() => {
    const filtered = scopedFilterAndSort({
      data: devices,
      scopeIds: globalAvailableLocationIds,
      filteredScopeIds: filteredLocations,
      scopeIdAccessor: (device) => (device.location?.locationId ? [device.location.locationId] : []),
    });

    return filtered.filter(({ name }) => name.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase()));
  }, [devices, globalAvailableLocationIds, filteredLocations]);

  return (
    <>
      <Table
        data={filteredDevices}
        isLoading={isLoading}
        isPaginated
        fullHeight
        fullHeightConfig={{
          offset: 500,
        }}
        clientPaginationConfig={{
          defaultRowsPerPage: 25,
        }}
        hasGlobalSearch
        globalSearchConfig={{
          label: t('Search'),
          position: settingsTenantLocation?.locationType !== 'single' ? 'right' : 'left',
          searchHandler: (term) => {
            setSearchTerm(term);
          },
        }}
        customToolbarRender={
          settingsTenantLocation && settingsTenantLocation?.locationType !== 'single'
            ? () => (
                <PhoneSettingsScopedLocationFilterMenu
                  selectedOptions={filteredLocations}
                  onChange={setFilteredLocations}
                />
              )
            : undefined
        }
        rowActions={{
          actions: [
            {
              label: '',
              Icon: EditSimpleIcon,
              disabled: ({ make }) => make !== DeviceMake_Enum.YEALINK,
              onClick: ({ name, deviceId }) => {
                setSelectedDeviceToEdit({
                  name,
                  deviceId,
                  assignableDeviceIds,
                  contactListsIds: deviceWithContactDirectories?.[deviceId].contactListIds,
                });
                triggerProps.onClick();
              },
            },
          ],
        }}
        colConfig={[
          {
            Header: t('Device Name'),
            id: 'device-name',
            accessor: ({ name }) => name,
          },
          {
            Header: t('Location'),
            id: 'location',
            accessor: (device) => device,
            cellRenderer: (device) => {
              if (!device.location) return;
              return <Chip.SingleChip>{device.location?.name}</Chip.SingleChip>;
            },
            disableSortBy: true,
            omit: settingsTenantLocation?.locationType === 'single',
            width: 150,
          },
          {
            Header: t('Assigned Contact Lists'),
            id: 'assigned-contact-lists',
            accessor: (cell) => cell,
            cellRenderer: ({ make, deviceId }) => {
              if (make !== DeviceMake_Enum.YEALINK) return '-';
              const lists = deviceWithContactDirectories?.[`${deviceId}`]?.contactLists;
              if (!lists?.length) return '-';
              return <Tooltip lists={lists} />;
            },
          },
        ]}
        wrapperStyle={css`
          margin-top: ${theme.spacing(2)};
          .table-toolbar {
            justify-content: space-between;
            align-items: start;
          }
          & > div:last-child {
            padding: ${theme.spacing(3, 0, 1, 0)};
          }
        `}
      />
      {modalProps.show && (
        <AssignContactDirectriesToDevices selectedDeviceToEdit={selectedDeviceToEdit} {...modalProps} />
      )}
    </>
  );
};

const Tooltip = ({ lists }: { lists?: ContactList[] }) => {
  const { Tooltip, tooltipProps, triggerProps: triggerTooltipProps } = useTooltip({ placement: 'top' });

  return (
    <>
      <div {...triggerTooltipProps} css={styles.truncate}>
        {lists?.map((list, i) => {
          return (
            <Text as='span' css={styles.truncate} key={list.id}>
              {list.name}
              {lists.length - 1 !== i && ','}&nbsp;
            </Text>
          );
        })}
      </div>
      <Tooltip {...tooltipProps}>
        {lists?.map((list, i) => {
          return (
            <Text color='white' as='span' key={list.id}>
              {list.name}
              {lists.length - 1 !== i && ','}&nbsp;
            </Text>
          );
        })}
      </Tooltip>
    </>
  );
};
